import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";

const INCLUDES = [
  "Marketing vendor & channel inventory",
  "Contract, service & invoice review",
  "Vendor deliverable verification",
  "Lead-definition & call-classification review",
  "Qualified-conversation analysis",
  "Referral & retained-matter review",
  "Preliminary vendor & channel scorecards",
  "Reporting-gap assessment",
  "Executive findings report & recommended actions",
  "One executive findings presentation",
];

export default function FreeAudit() {
  const [form, setForm] = useState({
    name: "", firm_name: "", email: "", phone: "",
    vendor_count: "", months_of_data: "", locations: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.firm_name) {
      return toast.error("Please add your name, firm and email.");
    }
    setLoading(true);
    try {
      await api.post("/public/audit-inquiry", form);
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1F33] text-white font-body">
      <Seo title="Request a consultation — RetainProof marketing assessment" description="Talk through your firm's marketing spend, vendors and retained-case data with RetainProof and find out whether an independent assessment would surface anything worth acting on." path="/consultation" />
      <header className="h-16 flex items-center justify-between px-6 sm:px-10 border-b border-white/10">
        <Link to="/" className="font-body font-bold text-2xl tracking-tight" data-testid="audit-brand-link">
          Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup>
        </Link>
        <Link to="/" data-testid="audit-nav-trial" className="font-mono-data text-xs uppercase tracking-widest text-zinc-400 hover:text-white">← Home</Link>
      </header>

      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-16 grid lg:grid-cols-2 gap-16">
        {/* Left: pitch */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#D5A44A] mb-4">
            Fully managed · One-time assessment
          </p>
          <h1 className="font-display text-4xl sm:text-5xl leading-[1.05]">
            RetainProof<sup className="text-[0.32em] align-super ml-[0.12em]">™</sup> Comprehensive<br />
            <span className="italic text-[#D5A44A]">Marketing Assessment</span>
          </h1>
          <p className="mt-5 text-white/60 text-base max-w-xl">
            For firms that want an in-depth, independent review without subscribing to software. Our team
            organizes and analyzes your vendor, spending, lead and retention data and delivers a clear
            executive assessment — nothing for your firm to implement or manage.
          </p>

          <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {INCLUDES.map((it) => (
              <li key={it} className="flex gap-2 text-sm text-white/70">
                <ShieldCheck className="h-4 w-4 text-[#D5A44A] shrink-0 mt-0.5" />
                {it}
              </li>
            ))}
          </ul>

          <p className="mt-8 text-xs text-white/40 max-w-md">
            A RetainProof™ assessment is a one-time engagement — no subscription and no long-term
            commitment. Pricing is quoted to the size of your review before any work begins.
          </p>
        </motion.div>

        {/* Right: inquiry form / success */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          {sent ? (
            <div className="border border-white/10 bg-white/5 p-10 text-center" data-testid="audit-inquiry-success">
              <CheckCircle2 className="h-12 w-12 text-[#0E7C7B] mx-auto" />
              <h2 className="font-display text-3xl mt-6">Request received.</h2>
              <p className="mt-4 text-white/60">
                Thank you, {form.name.split(" ")[0] || "there"}. Our team will review your firm's details and
                reach out within one business day to scope your Marketing Performance Assessment.
              </p>
              <Link to="/">
                <Button className="mt-8 bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-11 px-6">
                  Back to home
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-white/5 p-8 space-y-4" data-testid="audit-inquiry-form">
              <h2 className="font-display text-2xl">Request your assessment</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input required placeholder="Your name" value={form.name} onChange={set("name")}
                  data-testid="audit-name-input" className="rounded-none bg-white/5 border-white/15 text-white h-12 placeholder:text-white/40" />
                <Input required placeholder="Firm name" value={form.firm_name} onChange={set("firm_name")}
                  data-testid="audit-firm-input" className="rounded-none bg-white/5 border-white/15 text-white h-12 placeholder:text-white/40" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input required type="email" placeholder="Work email" value={form.email} onChange={set("email")}
                  data-testid="audit-email-input" className="rounded-none bg-white/5 border-white/15 text-white h-12 placeholder:text-white/40" />
                <Input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")}
                  data-testid="audit-phone-input" className="rounded-none bg-white/5 border-white/15 text-white h-12 placeholder:text-white/40" />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Input placeholder="# vendors" value={form.vendor_count} onChange={set("vendor_count")}
                  data-testid="audit-vendors-input" className="rounded-none bg-white/5 border-white/15 text-white h-12 placeholder:text-white/40" />
                <Input placeholder="Months of data" value={form.months_of_data} onChange={set("months_of_data")}
                  data-testid="audit-months-input" className="rounded-none bg-white/5 border-white/15 text-white h-12 placeholder:text-white/40" />
                <Input placeholder="# locations" value={form.locations} onChange={set("locations")}
                  data-testid="audit-locations-input" className="rounded-none bg-white/5 border-white/15 text-white h-12 placeholder:text-white/40" />
              </div>
              <Textarea placeholder="Anything we should know? (vendors, agencies, concerns…)" value={form.message} onChange={set("message")}
                data-testid="audit-message-input" className="rounded-none bg-white/5 border-white/15 text-white min-h-28 placeholder:text-white/40" />
              <Button type="submit" disabled={loading} data-testid="audit-submit-button"
                className="w-full bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 text-base font-medium group">
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                ) : (
                  <>Request my assessment <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></>
                )}
              </Button>
              <p className="flex items-center gap-2 text-xs text-white/40">
                <ShieldCheck className="h-3.5 w-3.5" /> No account required. We'll reach out to scope your engagement.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
